import { Component, EventEmitter, HostListener, Input, OnChanges, OnInit, Output, SimpleChanges, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { SalaService, SpaceFeatureIcon } from '../../services/sala.service';
import { NotificationService } from '../../services/notification.service';
import { DialogService } from '../../services/dialog.service';
import { EspacioExtra } from '../../models/sala.model';
import { FeatureIconSelectComponent } from '../ui/feature-icon-select.component';
import { InfoTipComponent } from '../ui/info-tip.component';

/** Extra en edición. En modo staged aún no existe en la BD (sin extra_id). */
export interface StagedExtra {
  name: string;
  description: string;
  price: number;
  icon: string | null;
  max_quantity: number | null;
  is_available: boolean;
}

interface ExtraItem extends StagedExtra {
  uid: number;
  extra_id?: number;
}

interface ExtraForm {
  icon: string;
  name: string;
  description: string;
  price: string;
  limited: boolean;
  max_quantity: string;
  is_available: boolean;
}

export const EXTRA_NAME_MAX = 100;
export const EXTRA_DESCRIPTION_MAX = 500;
export const EXTRA_PRICE_MAX = 99999.99;
export const EXTRA_MAX_QUANTITY_LIMIT = 999;
const DEFAULT_EXTRA_ICON = 'plus-circle';

/** Plantillas rápidas para empezar (se pueden editar antes de guardar). */
const EXTRA_TEMPLATES: Array<{ icon: string; name: string; description: string }> = [
  { icon: 'cup-hot', name: 'Café o bebidas calientes', description: 'Por persona' },
  { icon: 'cookie', name: 'Snacks', description: 'Bandeja para compartir' },
  { icon: 'projector', name: 'Proyector', description: 'Con cable HDMI' },
  { icon: 'car-front', name: 'Parqueo', description: 'Por vehículo' },
  { icon: 'printer', name: 'Impresiones', description: 'Por página' },
  { icon: 'mic', name: 'Micrófono', description: 'Inalámbrico' },
];

/**
 * Extras (servicios CON costo) de un espacio: café, proyector, parqueo...
 * Cada extra lleva icono, precio y, opcionalmente, una cantidad máxima por
 * reserva (para que un cliente no pida más de lo que el negocio puede dar;
 * el backend también la valida). Se ve igual al crear y al editar:
 *  - [staged]=true: el espacio aún no existe; emite la lista en (stagedChange).
 *  - [spaceId]: carga y guarda cada cambio contra el espacio.
 */
@Component({
  selector: 'app-space-extras-editor',
  standalone: true,
  imports: [CommonModule, FormsModule, FeatureIconSelectComponent, InfoTipComponent],
  template: `
    <section class="ex-section" [class.compact]="staged" aria-labelledby="ex-title">
      <header class="ex-head">
        <div>
          <h2 id="ex-title">Extras <small>servicios con costo</small> <span class="ex-counter" *ngIf="items().length">{{ items().length }}</span></h2>
          <p>Lo que el cliente puede sumar a su reserva pagando aparte. Si solo tienes unas cuantas unidades (por ejemplo 2 proyectores), fija un máximo por reserva.</p>
        </div>
        <button type="button" class="ex-btn primary" (click)="openNew()" [disabled]="loading()">
          <i class="bi bi-plus-lg" aria-hidden="true"></i> Agregar extra
        </button>
      </header>

      <p class="ex-state" *ngIf="loading()">Cargando extras…</p>
      <p class="ex-state ex-error" *ngIf="loadError()">{{ loadError() }} <button type="button" class="ex-link" (click)="reload()">Reintentar</button></p>

      <ng-container *ngIf="!loading() && !loadError()">
        <div class="ex-empty" *ngIf="!items().length">
          <i class="bi bi-bag-plus" aria-hidden="true"></i>
          <div>
            <strong>Aún no hay extras</strong>
            <span>Son opcionales. Empieza con una sugerencia (podrás cambiar todo antes de guardar):</span>
          </div>
          <div class="ex-templates">
            <button type="button" class="ex-chip" *ngFor="let t of templates" (click)="openNew(t)"><i class="bi" [ngClass]="'bi-' + t.icon" aria-hidden="true"></i>{{ t.name }}</button>
          </div>
        </div>

        <ul class="ex-grid" *ngIf="items().length" aria-label="Extras del espacio">
          <li *ngFor="let item of items(); trackBy: trackByUid" class="ex-card" [class.off]="!item.is_available">
            <span class="ex-icon"><i class="bi" [ngClass]="'bi-' + (item.icon || defaultIcon)" aria-hidden="true"></i></span>
            <div class="ex-text">
              <strong [title]="item.name">{{ item.name }}</strong>
              <small *ngIf="item.description" [title]="item.description">{{ item.description }}</small>
              <span class="ex-meta">
                <span class="ex-price">+{{ item.price | currency:'USD' }}</span>
                <span class="ex-tag" [class.limited]="item.max_quantity">
                  <i class="bi" [ngClass]="item.max_quantity ? 'bi-123' : 'bi-infinity'" aria-hidden="true"></i>
                  {{ item.max_quantity ? 'Máx. ' + item.max_quantity + ' por reserva' : 'Sin límite' }}
                </span>
                <span class="ex-tag off" *ngIf="!item.is_available"><i class="bi bi-eye-slash" aria-hidden="true"></i> Oculto</span>
              </span>
            </div>
            <div class="ex-actions">
              <button type="button" class="ex-mini" (click)="toggleAvailable(item)" [disabled]="busyUid() === item.uid"
                [attr.aria-label]="(item.is_available ? 'Ocultar ' : 'Mostrar ') + item.name"
                [title]="item.is_available ? 'Ocultar a los clientes' : 'Volver a ofrecer'"><i class="bi" [ngClass]="item.is_available ? 'bi-eye' : 'bi-eye-slash'"></i></button>
              <button type="button" class="ex-mini" (click)="openEdit(item)" [disabled]="busyUid() === item.uid" [attr.aria-label]="'Editar ' + item.name" title="Editar"><i class="bi bi-pencil"></i></button>
              <button type="button" class="ex-mini danger" (click)="remove(item)" [disabled]="busyUid() === item.uid" [attr.aria-label]="'Eliminar ' + item.name" title="Eliminar"><i class="bi bi-trash3"></i></button>
            </div>
          </li>
        </ul>
      </ng-container>
    </section>

    <!-- Modal: nuevo / editar extra -->
    <div class="ex-overlay" *ngIf="modalOpen()" (click)="closeModal()">
      <div class="ex-modal" role="dialog" aria-modal="true" aria-labelledby="ex-modal-title" (click)="$event.stopPropagation()" (keydown.enter)="onModalEnter($event)">
        <header>
          <h3 id="ex-modal-title">{{ editingUid === null ? 'Nuevo extra' : 'Editar extra' }}</h3>
          <button type="button" class="ex-mini" (click)="closeModal()" aria-label="Cerrar"><i class="bi bi-x-lg"></i></button>
        </header>

        <div class="ex-form">
          <div class="ex-field span-2">
            <label class="ex-label" for="ex-icon-input">Icono <small>(opcional)</small></label>
            <app-feature-icon-select inputId="ex-icon-input" [icons]="icons()" [value]="form.icon" (valueChange)="onIconPicked($event)"></app-feature-icon-select>
          </div>

          <div class="ex-field span-2">
            <label class="ex-label" for="ex-name">Nombre *</label>
            <input id="ex-name" class="ex-input" [class.invalid]="showError('name')" [(ngModel)]="form.name" (blur)="touch('name')" [maxlength]="nameMax" placeholder="Ej. Café de grano" data-no-limit-hint>
            <small class="ex-err" *ngIf="showError('name')">{{ errors()['name'] }}</small>
          </div>

          <div class="ex-field span-2">
            <label class="ex-label" for="ex-desc">Detalle <small>(opcional)</small></label>
            <input id="ex-desc" class="ex-input" [class.invalid]="showError('description')" [(ngModel)]="form.description" (blur)="touch('description')" [maxlength]="descriptionMax" placeholder="Ej. Por persona · incluye azúcar y leche" data-no-limit-hint>
            <small class="ex-err" *ngIf="showError('description')">{{ errors()['description'] }}</small>
          </div>

          <div class="ex-field">
            <label class="ex-label" for="ex-price">Precio por unidad (USD) *</label>
            <div class="ex-money" [class.invalid]="showError('price')">
              <span aria-hidden="true">$</span>
              <input id="ex-price" type="number" inputmode="decimal" min="0" [max]="priceMax" step="0.01" class="ex-input" [(ngModel)]="form.price" (blur)="touch('price')" placeholder="0.00" [attr.data-max-message]="'El precio máximo es $' + priceMax">
            </div>
            <small class="ex-err" *ngIf="showError('price')">{{ errors()['price'] }}</small>
          </div>

          <div class="ex-field">
            <span class="ex-label">Cantidad por reserva <app-info-tip text="Con máximo, el cliente no puede pedir más unidades de las que indiques por reserva (ej. si solo tienes 2 proyectores). Al llegar al tope verá un aviso."></app-info-tip></span>
            <div class="ex-segment" role="radiogroup" aria-label="Cantidad por reserva">
              <button type="button" role="radio" [attr.aria-checked]="!form.limited" [class.on]="!form.limited" (click)="setLimited(false)"><i class="bi bi-infinity" aria-hidden="true"></i> Sin límite</button>
              <button type="button" role="radio" [attr.aria-checked]="form.limited" [class.on]="form.limited" (click)="setLimited(true)"><i class="bi bi-123" aria-hidden="true"></i> Con máximo</button>
            </div>
            <div class="ex-max" *ngIf="form.limited">
              <label for="ex-max">Máximo</label>
              <input id="ex-max" type="number" inputmode="numeric" min="1" [max]="maxQuantityLimit" step="1" class="ex-input" [class.invalid]="showError('max_quantity')" [(ngModel)]="form.max_quantity" (blur)="touch('max_quantity')" placeholder="Ej. 2" [attr.data-max-message]="'El máximo permitido es ' + maxQuantityLimit">
            </div>
            <small class="ex-err" *ngIf="showError('max_quantity')">{{ errors()['max_quantity'] }}</small>
          </div>

          <label class="ex-check span-2">
            <input type="checkbox" [(ngModel)]="form.is_available">
            <span><strong>Disponible para los clientes</strong><small>Desmárcalo para ocultarlo sin borrarlo.</small></span>
          </label>

          <div class="ex-preview span-2" aria-label="Vista previa en la ficha del espacio">
            <span class="ex-preview-label">Así lo verá el cliente</span>
            <div class="ex-preview-card">
              <span class="ex-icon"><i class="bi" [ngClass]="'bi-' + (form.icon || defaultIcon)" aria-hidden="true"></i></span>
              <span class="ex-text">
                <strong>{{ form.name.trim() || 'Nombre del extra' }}</strong>
                <small>{{ form.description.trim() || (form.limited && form.max_quantity ? 'Máximo ' + form.max_quantity + ' por reserva' : 'Se suma al total de la reserva') }}</small>
              </span>
              <span class="ex-price">+{{ previewPrice() | currency:'USD' }}</span>
            </div>
          </div>
        </div>

        <p class="ex-error" *ngIf="modalError()" role="alert">{{ modalError() }}</p>
        <footer>
          <button type="button" class="ex-btn ghost" (click)="closeModal()">Cancelar</button>
          <button type="button" class="ex-btn primary" (click)="submitModal()" [disabled]="modalBusy()">{{ modalBusy() ? 'Guardando…' : (editingUid === null ? 'Agregar extra' : 'Guardar cambios') }}</button>
        </footer>
      </div>
    </div>
  `,
  styles: [`
    :host { display: block; }
    .ex-section { margin-bottom: 1.1rem; padding: 1.35rem; border-radius: 16px; border: 1px solid var(--ts-border-strong); background: var(--ts-bg-elevated); box-shadow: 0 10px 24px var(--ts-shadow-gray-1000-a16); }
    .ex-section.compact { margin: 0; padding: 0; border: 0; box-shadow: none; background: transparent; }
    .ex-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem; padding-bottom: .9rem; margin-bottom: .9rem; border-bottom: 1px solid var(--ts-border); }
    .ex-head h2 { display: flex; align-items: baseline; flex-wrap: wrap; gap: .5rem; margin: 0 0 .3rem; color: var(--ts-text); font-size: 1.1rem; }
    .ex-head h2 small { color: var(--ts-text-muted); font-size: .78rem; font-weight: 600; }
    .compact .ex-head h2 { font-size: 1rem; }
    .ex-head p { margin: 0; max-width: 64ch; color: var(--ts-text-soft); font-size: .84rem; line-height: 1.5; }
    .ex-counter { padding: .05rem .5rem; border-radius: 999px; border: 1px solid var(--ts-border); color: var(--ts-text-soft); font-size: .72rem; font-weight: 750; }
    .ex-state { margin: .4rem 0; color: var(--ts-text-muted); font-size: .86rem; }
    .ex-error, .ex-err { color: var(--ts-danger-text, #ff8a8a); font-size: .8rem; font-weight: 600; }
    .ex-err { display: block; margin-top: .3rem; }
    .ex-link { padding: 0; border: 0; background: none; color: var(--ts-text-blue-250-3); font: inherit; font-weight: 750; cursor: pointer; }
    .ex-empty { display: grid; grid-template-columns: auto minmax(0, 1fr); gap: .6rem .9rem; align-items: center; padding: 1rem; border: 1px dashed var(--ts-border-strong); border-radius: 12px; background: var(--ts-bg-blue-400-a5); }
    .ex-empty > i { color: var(--ts-text-blue-250-3); font-size: 1.5rem; }
    .ex-empty > div:not(.ex-templates) { display: grid; gap: .15rem; }
    .ex-empty strong { color: var(--ts-text); font-size: .92rem; }
    .ex-empty span { color: var(--ts-text-soft); font-size: .8rem; }
    .ex-templates { grid-column: 1 / -1; display: flex; flex-wrap: wrap; gap: .4rem; }
    .ex-chip { width: auto !important; max-width: max-content !important; flex: none; display: inline-flex; align-items: center; gap: .4rem; height: 32px; min-height: 32px; padding: 0 .7rem; border: 1px solid var(--ts-border-strong); border-radius: 999px; color: var(--ts-text-soft); background: var(--ts-surface); font: inherit; font-size: .8rem; font-weight: 650; cursor: pointer; }
    .ex-chip i { color: var(--ts-text-blue-250-3); }
    .ex-chip:hover { border-color: var(--ts-primary); color: var(--ts-text); }
    .ex-grid { list-style: none; margin: 0; padding: 0; display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: .6rem; }
    .ex-card { display: grid; grid-template-columns: auto minmax(0, 1fr) auto; align-items: center; gap: .65rem; padding: .55rem .6rem; border: 1px solid var(--ts-border); border-radius: 12px; background: var(--ts-surface); transition: border-color .15s ease; }
    .ex-card:hover { border-color: var(--ts-border-strong); }
    .ex-card.off { opacity: .62; }
    .ex-icon { width: 34px; height: 34px; display: grid; place-items: center; flex: none; border-radius: 9px; color: var(--ts-text-blue-250-3); background: var(--ts-bg-blue-400-a14); font-size: 1rem; }
    .ex-text { display: grid; gap: .1rem; min-width: 0; }
    .ex-text strong { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: var(--ts-text); font-size: .9rem; }
    .ex-text small { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: var(--ts-text-muted); font-size: .76rem; }
    .ex-meta { display: flex; flex-wrap: wrap; align-items: center; gap: .35rem; margin-top: .25rem; }
    .ex-price { color: var(--ts-text-green-350); font-weight: 800; font-size: .85rem; font-variant-numeric: tabular-nums; white-space: nowrap; }
    .ex-tag { display: inline-flex; align-items: center; gap: .25rem; padding: .1rem .45rem; border-radius: 999px; border: 1px solid var(--ts-border); color: var(--ts-text-soft); font-size: .7rem; font-weight: 700; white-space: nowrap; }
    .ex-tag.limited { color: var(--ts-text-orange-250); border-color: var(--ts-border-orange-250-a45); }
    .ex-tag.off { color: var(--ts-text-muted); }
    .ex-actions { display: flex; gap: .3rem; }
    .ex-btn, .ex-mini { display: inline-flex; align-items: center; justify-content: center; gap: .4rem; border-radius: 9px; border: 1px solid var(--ts-border-strong); color: var(--ts-text); background: var(--ts-surface); font: inherit; cursor: pointer; }
    .ex-btn { height: 36px; min-height: 36px; padding: 0 .9rem; font-size: .82rem; font-weight: 700; white-space: nowrap; }
    .ex-btn.primary { border-color: transparent; color: var(--ts-text-on-accent); background: linear-gradient(135deg, var(--ts-bg-blue-500), var(--ts-bg-sky-450)); }
    .ex-btn.ghost { background: transparent; }
    .ex-mini { width: 30px; height: 30px; min-height: 0; padding: 0; font-size: .8rem; }
    .ex-actions .ex-mini { border-color: transparent; background: transparent; color: var(--ts-text-muted); }
    .ex-actions .ex-mini:hover:not(:disabled) { border-color: var(--ts-border-strong); color: var(--ts-text); background: var(--ts-surface); }
    .ex-mini.danger:hover { border-color: var(--ts-danger); color: var(--ts-danger-text); }
    .ex-btn:disabled, .ex-mini:disabled { opacity: .45; cursor: not-allowed; }
    .ex-overlay { position: fixed; inset: 0; z-index: 3100; display: grid; place-items: center; padding: 1rem; background: var(--ts-bg-navy-950-a78-4); }
    .ex-modal { width: min(620px, 100%); max-height: calc(100dvh - 2rem); overflow-y: auto; box-sizing: border-box; display: flex; flex-direction: column; gap: .6rem; padding: 1.25rem; border-radius: 16px; border: 1px solid var(--ts-border-strong); background: var(--ts-bg-elevated); box-shadow: 0 26px 72px var(--ts-shadow-gray-1000-a40); }
    .ex-modal header { display: flex; align-items: center; justify-content: space-between; gap: 1rem; }
    .ex-modal h3 { margin: 0; color: var(--ts-text); font-size: 1.08rem; }
    .ex-modal footer { display: flex; justify-content: flex-end; gap: .5rem; margin-top: .3rem; }
    .ex-form { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: .8rem 1rem; align-items: start; }
    .ex-field { display: grid; min-width: 0; }
    .span-2 { grid-column: 1 / -1; }
    .ex-label { display: flex; align-items: center; gap: .25rem; margin-bottom: .35rem; color: var(--ts-text-soft); font-size: .8rem; font-weight: 750; }
    .ex-label small { font-weight: 500; color: var(--ts-text-muted); }
    .ex-input { width: 100%; box-sizing: border-box; height: 40px; min-height: 40px; padding: .6rem .75rem; border-radius: 9px; border: 1px solid var(--ts-border-strong); color: var(--ts-text); background: var(--ts-input-bg); font: inherit; font-size: .9rem; }
    .ex-input:focus { outline: none; border-color: var(--ts-primary); box-shadow: none; }
    .ex-input.invalid, .ex-money.invalid { border-color: var(--ts-danger, #f05259) !important; }
    .ex-money { display: flex; align-items: center; border: 1px solid var(--ts-border-strong); border-radius: 9px; background: var(--ts-input-bg); }
    .ex-money span { padding: 0 0 0 .75rem; color: var(--ts-text-muted); font-weight: 700; }
    .ex-money .ex-input { border: 0 !important; outline: none !important; background: transparent !important; box-shadow: none !important; height: 38px !important; min-height: 38px !important; }
    .ex-money:focus-within { border-color: var(--ts-primary); box-shadow: none; }
    .ex-segment { display: grid; grid-template-columns: 1fr 1fr; gap: .25rem; padding: .2rem; border: 1px solid var(--ts-border-strong); border-radius: 10px; background: var(--ts-surface); }
    .ex-segment button { display: inline-flex; align-items: center; justify-content: center; gap: .35rem; min-height: 38px; border: 0; border-radius: 8px; color: var(--ts-text-soft); background: transparent; font: inherit; font-size: .82rem; font-weight: 700; cursor: pointer; }
    .ex-segment button.on { color: var(--ts-text-on-accent); background: linear-gradient(135deg, var(--ts-bg-blue-500), var(--ts-bg-sky-450)); }
    .ex-max { display: flex; align-items: center; gap: .5rem; margin-top: .5rem; }
    .ex-max label { color: var(--ts-text-soft); font-size: .8rem; font-weight: 700; }
    .ex-max .ex-input { max-width: 120px; }
    .ex-check { display: flex; align-items: flex-start; gap: .6rem; padding: .65rem .75rem; border: 1px solid var(--ts-border); border-radius: 10px; cursor: pointer; }
    .ex-check input { margin-top: .2rem; width: 17px; height: 17px; accent-color: var(--ts-primary); }
    .ex-check span { display: grid; gap: .1rem; }
    .ex-check strong { color: var(--ts-text); font-size: .86rem; }
    .ex-check small { color: var(--ts-text-muted); font-size: .76rem; }
    .ex-preview { display: grid; gap: .35rem; }
    .ex-preview-label { color: var(--ts-text-muted); font-size: .72rem; font-weight: 750; letter-spacing: .04em; text-transform: uppercase; }
    .ex-preview-card { display: grid; grid-template-columns: auto minmax(0, 1fr) auto; align-items: center; gap: .75rem; padding: .7rem .8rem; border: 1px dashed var(--ts-border-strong); border-radius: 12px; background: var(--ts-surface); }
    @media (max-width: 860px) { .ex-grid { grid-template-columns: 1fr; } }
    @media (max-width: 560px) {
      .ex-head { flex-direction: column; }
      .ex-head .ex-btn { width: 100%; }
      .ex-form { grid-template-columns: 1fr; }
      .ex-card { grid-template-columns: auto minmax(0, 1fr); }
      .ex-actions { grid-column: 1 / -1; justify-content: flex-end; }
    }
  `],
})
export class SpaceExtrasEditorComponent implements OnInit, OnChanges {
  @Input() spaceId: number | null = null;
  /** true en la creación del espacio: no guarda, solo emite la lista. */
  @Input() staged = false;
  /** Lista inicial en modo staged (p. ej. al retomar un borrador). */
  @Input() initialStaged: StagedExtra[] | null = null;
  @Output() stagedChange = new EventEmitter<StagedExtra[]>();
  /** Modo con spaceId: la lista guardada cambió (para refrescar la vista). */
  @Output() extrasChange = new EventEmitter<EspacioExtra[]>();

  private readonly sala = inject(SalaService);
  private readonly notifications = inject(NotificationService);
  private readonly dialogs = inject(DialogService);

  readonly templates = EXTRA_TEMPLATES;
  readonly defaultIcon = DEFAULT_EXTRA_ICON;
  readonly nameMax = EXTRA_NAME_MAX;
  readonly descriptionMax = EXTRA_DESCRIPTION_MAX;
  readonly priceMax = EXTRA_PRICE_MAX;
  readonly maxQuantityLimit = EXTRA_MAX_QUANTITY_LIMIT;

  readonly items = signal<ExtraItem[]>([]);
  readonly icons = signal<SpaceFeatureIcon[]>([]);
  readonly loading = signal(false);
  readonly loadError = signal('');
  readonly busyUid = signal<number | null>(null);

  readonly modalOpen = signal(false);
  readonly modalBusy = signal(false);
  readonly modalError = signal('');
  readonly errors = signal<Record<string, string>>({});
  form: ExtraForm = this.emptyForm();
  editingUid: number | null = null;
  private touched = new Set<string>();
  private submitted = false;
  private lastAutoName = '';
  private nextUid = 1;

  ngOnInit(): void {
    if (this.staged) {
      if (this.initialStaged?.length) this.items.set(this.initialStaged.map(extra => this.toItem(extra)));
      void this.loadIcons();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['spaceId'] && this.spaceId && !this.staged) void this.reload();
    if (changes['initialStaged'] && !changes['initialStaged'].firstChange && this.staged) {
      this.items.set((this.initialStaged || []).map(extra => this.toItem(extra)));
    }
  }

  private async loadIcons(): Promise<void> {
    if (this.icons().length) return;
    try { this.icons.set(await firstValueFrom(this.sala.getSpaceFeatureIcons())); } catch { /* el icono es opcional */ }
  }

  async reload(): Promise<void> {
    if (!this.spaceId) return;
    this.loading.set(true);
    this.loadError.set('');
    try {
      const [response] = await Promise.all([firstValueFrom(this.sala.getExtras(this.spaceId)), this.loadIcons()]);
      // El backend responde { extras: [...] } (el tipo del servicio dice arreglo).
      const list: EspacioExtra[] = Array.isArray(response) ? response : ((response as any)?.extras ?? []);
      this.items.set(list.map(extra => this.toItem(extra)));
    } catch {
      this.loadError.set('No se pudieron cargar los extras.');
    } finally {
      this.loading.set(false);
    }
  }

  private toItem(extra: Partial<EspacioExtra> & { name: string }): ExtraItem {
    return {
      uid: this.nextUid++,
      extra_id: extra.extra_id,
      name: extra.name,
      description: extra.description || '',
      price: Number(extra.price) || 0,
      icon: extra.icon || null,
      max_quantity: extra.max_quantity ? Number(extra.max_quantity) : null,
      is_available: extra.is_available !== false,
    };
  }

  private toStaged(item: ExtraItem): StagedExtra {
    const { name, description, price, icon, max_quantity, is_available } = item;
    return { name, description, price, icon, max_quantity, is_available };
  }

  private emitChanges(): void {
    if (this.staged) {
      this.stagedChange.emit(this.items().map(item => this.toStaged(item)));
    } else {
      this.extrasChange.emit(this.items().map(item => ({
        extra_id: item.extra_id, name: item.name, description: item.description, price: item.price.toFixed(2),
        icon: item.icon, max_quantity: item.max_quantity, is_available: item.is_available,
      })));
    }
  }

  // ---- Modal ----
  private emptyForm(): ExtraForm {
    return { icon: '', name: '', description: '', price: '', limited: false, max_quantity: '', is_available: true };
  }

  openNew(template?: { icon: string; name: string; description: string }): void {
    this.form = { ...this.emptyForm(), icon: template?.icon || '', name: template?.name || '', description: template?.description || '' };
    this.lastAutoName = template?.name || '';
    this.editingUid = null;
    this.openModal();
  }

  openEdit(item: ExtraItem): void {
    this.form = {
      icon: item.icon || '', name: item.name, description: item.description, price: String(item.price),
      limited: !!item.max_quantity, max_quantity: item.max_quantity ? String(item.max_quantity) : '', is_available: item.is_available,
    };
    this.lastAutoName = '';
    this.editingUid = item.uid;
    this.openModal();
  }

  private openModal(): void {
    this.touched.clear();
    this.submitted = false;
    this.errors.set({});
    this.modalError.set('');
    this.modalOpen.set(true);
    void this.loadIcons();
    setTimeout(() => document.getElementById(this.form.name ? 'ex-price' : 'ex-name')?.focus(), 0);
  }

  closeModal(): void { if (!this.modalBusy()) this.modalOpen.set(false); }

  @HostListener('document:keydown.escape')
  onEscape(): void { if (this.modalOpen()) this.closeModal(); }

  /** Enter nunca llega al formulario que contiene al editor (crear espacio). */
  onModalEnter(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    const target = event.target as HTMLElement;
    if (target.tagName === 'INPUT' && target.id !== 'ex-icon-input') void this.submitModal();
  }

  onIconPicked(icon: SpaceFeatureIcon): void {
    this.form.icon = icon.id;
    if (!this.form.name.trim() || this.form.name === this.lastAutoName) {
      this.form.name = icon.label;
      this.lastAutoName = icon.label;
    }
    this.validate();
  }

  setLimited(limited: boolean): void {
    this.form.limited = limited;
    if (limited) setTimeout(() => document.getElementById('ex-max')?.focus(), 0);
    else this.touched.delete('max_quantity');
    this.validate();
  }

  touch(field: string): void {
    this.touched.add(field);
    this.validate();
  }

  /** Los errores se muestran solo en campos ya tocados o tras intentar guardar. */
  showError(field: string): boolean {
    return !!this.errors()[field] && (this.submitted || this.touched.has(field));
  }

  previewPrice(): number {
    const price = Number(this.form.price);
    return Number.isFinite(price) && price > 0 ? price : 0;
  }

  private validate(): Record<string, string> {
    const errors: Record<string, string> = {};
    const name = this.form.name.replace(/\s+/g, ' ').trim();
    if (!name) errors['name'] = 'Escribe el nombre del extra.';
    else if (name.length > EXTRA_NAME_MAX) errors['name'] = `Máximo ${EXTRA_NAME_MAX} caracteres.`;
    else if (this.items().some(item => item.uid !== this.editingUid && item.name.trim().toLocaleLowerCase('es') === name.toLocaleLowerCase('es'))) {
      errors['name'] = 'Ya tienes un extra con ese nombre en este espacio.';
    }
    if (this.form.description.trim().length > EXTRA_DESCRIPTION_MAX) errors['description'] = `Máximo ${EXTRA_DESCRIPTION_MAX} caracteres.`;
    const rawPrice = String(this.form.price ?? '').trim();
    const price = Number(rawPrice);
    if (!rawPrice) errors['price'] = 'Indica el precio (usa 0 si es sin costo).';
    else if (!Number.isFinite(price) || price < 0) errors['price'] = 'El precio debe ser 0 o mayor.';
    else if (price > EXTRA_PRICE_MAX) errors['price'] = `El precio máximo es $${EXTRA_PRICE_MAX}.`;
    else if (!/^\d+(\.\d{1,2})?$/.test(rawPrice)) errors['price'] = 'Usa como máximo 2 decimales.';
    if (this.form.limited) {
      const rawMax = String(this.form.max_quantity ?? '').trim();
      const max = Number(rawMax);
      if (!rawMax) errors['max_quantity'] = 'Indica el máximo o elige «Sin límite».';
      else if (!Number.isInteger(max) || max < 1) errors['max_quantity'] = 'Usa un número entero desde 1.';
      else if (max > EXTRA_MAX_QUANTITY_LIMIT) errors['max_quantity'] = `El máximo permitido es ${EXTRA_MAX_QUANTITY_LIMIT}.`;
    }
    this.errors.set(errors);
    return errors;
  }

  async submitModal(): Promise<void> {
    this.submitted = true;
    this.modalError.set('');
    const errors = this.validate();
    const firstError = ['name', 'description', 'price', 'max_quantity'].find(field => errors[field]);
    if (firstError) {
      document.getElementById(firstError === 'max_quantity' ? 'ex-max' : firstError === 'description' ? 'ex-desc' : `ex-${firstError}`)?.focus();
      return;
    }
    const data: StagedExtra = {
      name: this.form.name.replace(/\s+/g, ' ').trim(),
      description: this.form.description.trim(),
      price: Math.round(Number(this.form.price) * 100) / 100,
      icon: this.form.icon || null,
      max_quantity: this.form.limited ? Number(this.form.max_quantity) : null,
      is_available: this.form.is_available,
    };

    if (this.staged) {
      if (this.editingUid === null) this.items.update(list => [...list, { ...data, uid: this.nextUid++ }]);
      else this.items.update(list => list.map(item => item.uid === this.editingUid ? { ...item, ...data } : item));
      this.emitChanges();
      this.modalOpen.set(false);
      return;
    }

    if (!this.spaceId) return;
    this.modalBusy.set(true);
    try {
      const payload = { ...data, description: data.description || undefined };
      if (this.editingUid === null) {
        const created = await firstValueFrom(this.sala.crearExtra(this.spaceId, payload));
        this.items.update(list => [...list, this.toItem(created)]);
        this.notifications.success('Extra agregado.');
      } else {
        const current = this.items().find(item => item.uid === this.editingUid);
        if (!current?.extra_id) return;
        const updated = await firstValueFrom(this.sala.actualizarExtra(current.extra_id, { ...data, description: data.description }));
        this.items.update(list => list.map(item => item.uid === this.editingUid ? { ...this.toItem(updated), uid: item.uid } : item));
        this.notifications.success('Extra actualizado.');
      }
      this.emitChanges();
      this.modalOpen.set(false);
    } catch (error: any) {
      this.modalError.set(typeof error === 'string' ? error : 'No se pudo guardar el extra.');
    } finally {
      this.modalBusy.set(false);
    }
  }

  async toggleAvailable(item: ExtraItem): Promise<void> {
    const next = !item.is_available;
    if (this.staged || !item.extra_id) {
      this.items.update(list => list.map(entry => entry.uid === item.uid ? { ...entry, is_available: next } : entry));
      this.emitChanges();
      return;
    }
    this.busyUid.set(item.uid);
    try {
      const updated = await firstValueFrom(this.sala.actualizarExtra(item.extra_id, { is_available: next }));
      this.items.update(list => list.map(entry => entry.uid === item.uid ? { ...this.toItem(updated), uid: entry.uid } : entry));
      this.emitChanges();
      this.notifications.success(next ? `«${item.name}» vuelve a ofrecerse a los clientes.` : `«${item.name}» quedó oculto para los clientes.`);
    } catch (error: any) {
      this.notifications.error(typeof error === 'string' ? error : 'No se pudo actualizar el extra.');
    } finally {
      this.busyUid.set(null);
    }
  }

  async remove(item: ExtraItem): Promise<void> {
    if (this.staged || !item.extra_id) {
      this.items.update(list => list.filter(entry => entry.uid !== item.uid));
      this.emitChanges();
      return;
    }
    const ok = await this.dialogs.confirm({
      tone: 'danger',
      title: `Eliminar «${item.name}»`,
      message: 'Dejará de ofrecerse en este espacio. Si ya se usó en reservas, te pediremos ocultarlo en su lugar para conservar el historial.',
      confirmText: 'Eliminar',
    });
    if (!ok) return;
    this.busyUid.set(item.uid);
    try {
      await firstValueFrom(this.sala.eliminarExtra(item.extra_id));
      this.items.update(list => list.filter(entry => entry.uid !== item.uid));
      this.emitChanges();
      this.notifications.success('Extra eliminado.');
    } catch (error: any) {
      const message = typeof error === 'string' ? error : 'No se pudo eliminar el extra.';
      if (/Desactívalo/.test(message) && item.is_available) {
        const hide = await this.dialogs.confirm({ tone: 'warning', title: 'No se puede eliminar', message, confirmText: 'Ocultarlo', cancelText: 'Dejarlo como está' });
        if (hide) { this.busyUid.set(null); await this.toggleAvailable(item); return; }
      } else {
        this.notifications.error(message);
      }
    } finally {
      this.busyUid.set(null);
    }
  }

  trackByUid(_index: number, item: ExtraItem): number { return item.uid; }
}
